import { BaseService } from './BaseService';
import { API_ENDPOINTS } from './config';
import { ChatThread, ChatMessage } from '../../contexts/ChatContext';
import { chatThreadToBackend, chatThreadFromBackend } from '../../utils/fieldMapping';

export class ThreadService extends BaseService {
  /**
   * Get all threads for a specific user
   */
  static async getByUser(userId: string): Promise<ChatThread[]> {
    try {
      // Validate userId format (should be UUID)
      if (!userId || typeof userId !== 'string') {
        console.error('[ThreadService.getByUser] Invalid userId:', userId);
        return [];
      }
      
      const backendThreads = await this.get<any>(API_ENDPOINTS.THREADS.BY_USER(userId));
      
      // Log raw backend response for debugging
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 [ThreadService.getByUser] Raw backend response:', {
          userId,
          responseType: typeof backendThreads,
          isArray: Array.isArray(backendThreads),
          response: backendThreads
        });
      }
      
      // Handle different response formats
      let threadsArray: any[] = [];
      
      if (Array.isArray(backendThreads)) {
        // Direct array response
        threadsArray = backendThreads;
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ [ThreadService.getByUser] Direct array response with', threadsArray.length, 'threads');
        }
      } else if (backendThreads && typeof backendThreads === 'object') {
        // Check if it's wrapped in an object (e.g., { threads: [...] })
        if (Array.isArray(backendThreads.threads)) {
          threadsArray = backendThreads.threads;
        } else if (Array.isArray(backendThreads.data)) {
          threadsArray = backendThreads.data;
        } else if (backendThreads.data && typeof backendThreads.data === 'object') {
          // Backend returns: {success: true, data: {threads: [...], count, limit, offset}}
          if (Array.isArray(backendThreads.data.threads)) {
            threadsArray = backendThreads.data.threads;
            if (process.env.NODE_ENV === 'development') {
              console.log('✅ [ThreadService.getByUser] Found threads in data.threads:', {
                count: threadsArray.length,
                totalCount: backendThreads.data.count,
                limit: backendThreads.data.limit,
                offset: backendThreads.data.offset
              });
            }
          } else if (Array.isArray(backendThreads.data.data)) {
            // Nested data.data structure
            threadsArray = backendThreads.data.data;
          }
        }
        
        // Check for error responses after checking data structure
        if (threadsArray.length === 0) {
          if (backendThreads.detail || backendThreads.error || backendThreads.message) {
          // Error response object - log and return empty
          console.warn('[ThreadService.getByUser] Error response from API:', {
            userId,
            error: backendThreads.detail || backendThreads.error || backendThreads.message
          });
          return [];
        } else {
            // No threads found and no error - log for debugging
          if (process.env.NODE_ENV === 'development') {
            console.warn('[ThreadService.getByUser] Response object has no threads/data array:', {
              userId,
              objectKeys: Object.keys(backendThreads),
                response: backendThreads,
                responseString: JSON.stringify(backendThreads, null, 2)
            });
          }
          return [];
          }
        }
      } else if (backendThreads === null || backendThreads === undefined) {
        // Empty response - return empty array
        if (process.env.NODE_ENV === 'development') {
          console.log('⚠️ [ThreadService.getByUser] Empty response (null/undefined) for userId:', userId);
        }
        return [];
      } else {
        // Unexpected type
        console.error('[ThreadService.getByUser] Response is not an array or object:', {
          userId,
          responseType: typeof backendThreads,
          response: backendThreads
        });
        return [];
      }
      
      // Log raw thread data before conversion to debug missing name field
      if (process.env.NODE_ENV === 'development' && threadsArray.length > 0) {
        console.log('🔍 [ThreadService.getByUser] Raw thread data (first thread sample):', {
          sampleThread: threadsArray[0],
          sampleThreadKeys: Object.keys(threadsArray[0]),
          hasName: 'name' in threadsArray[0],
          nameValue: threadsArray[0].name,
          allThreadsHaveName: threadsArray.every(t => 'name' in t),
          threadsWithoutName: threadsArray.filter(t => !('name' in t) || !t.name).map(t => ({ id: t.id, keys: Object.keys(t) }))
        });
      }
      
      const convertedThreads = threadsArray.map(thread => {
        const converted = chatThreadFromBackend(thread) as ChatThread;
        
        // Explicitly ensure name is preserved from multiple possible locations
        // Backend may use: name, title, or thread_name (for schema compatibility)
        // If name is "Untitled Thread", try to use description or generate a better name
        if (!converted.name || converted.name === 'Untitled Thread') {
          // Try to get name from various possible locations and field names
          // Prefer non-"Untitled Thread" values
          let threadName: string | undefined;
          
          // Check name field first
          if (thread.name && thread.name !== 'Untitled Thread') {
            threadName = thread.name;
          } else if (thread.result && typeof thread.result === 'object' && thread.result.name && thread.result.name !== 'Untitled Thread') {
            threadName = thread.result.name;
          } else if (thread.data && typeof thread.data === 'object' && thread.data.name && thread.data.name !== 'Untitled Thread') {
            threadName = thread.data.name;
          }
          // Check title field (alternative field name)
          else if (thread.title && thread.title !== 'Untitled Thread') {
            threadName = thread.title;
          } else if (thread.result && typeof thread.result === 'object' && thread.result.title && thread.result.title !== 'Untitled Thread') {
            threadName = thread.result.title;
          } else if (thread.data && typeof thread.data === 'object' && thread.data.title && thread.data.title !== 'Untitled Thread') {
            threadName = thread.data.title;
          }
          // Check thread_name field (alternative field name)
          else if (thread.thread_name && thread.thread_name !== 'Untitled Thread') {
            threadName = thread.thread_name;
          } else if (thread.result && typeof thread.result === 'object' && thread.result.thread_name && thread.result.thread_name !== 'Untitled Thread') {
            threadName = thread.result.thread_name;
          } else if (thread.data && typeof thread.data === 'object' && thread.data.thread_name && thread.data.thread_name !== 'Untitled Thread') {
            threadName = thread.data.thread_name;
          }
          // If name is "Untitled Thread", try to use description as fallback
          else if (thread.description && typeof thread.description === 'string' && thread.description.trim() !== '') {
            // Use description, but truncate if too long
            threadName = thread.description.length > 50 ? thread.description.substring(0, 50) + '...' : thread.description;
          } else if (thread.result && typeof thread.result === 'object' && thread.result.description && typeof thread.result.description === 'string' && thread.result.description.trim() !== '') {
            threadName = thread.result.description.length > 50 ? thread.result.description.substring(0, 50) + '...' : thread.result.description;
          } else if (thread.data && typeof thread.data === 'object' && thread.data.description && typeof thread.data.description === 'string' && thread.data.description.trim() !== '') {
            threadName = thread.data.description.length > 50 ? thread.data.description.substring(0, 50) + '...' : thread.data.description;
          }
          // Fallback to name even if it's "Untitled Thread" (better than nothing)
          else if (thread.name) {
            threadName = thread.name;
          } else if (thread.result && typeof thread.result === 'object' && thread.result.name) {
            threadName = thread.result.name;
          } else if (thread.data && typeof thread.data === 'object' && thread.data.name) {
            threadName = thread.data.name;
          }
          // Generate a fallback name from thread ID if all else fails
          else {
            threadName = `Thread ${converted.id.substring(0, 8)}`;
          }
          
          if (threadName) {
            converted.name = threadName;
          }
        }
        
        // Log if name was "Untitled Thread" or missing and we had to use a fallback
        if (process.env.NODE_ENV === 'development') {
          const originalName = thread.name || thread.result?.name || thread.data?.name;
          const finalName = converted.name;
          const usedDescription = thread.description && finalName === (thread.description.length > 50 ? thread.description.substring(0, 50) + '...' : thread.description);
          
          if (originalName === 'Untitled Thread' && finalName !== 'Untitled Thread') {
            console.log('✅ [ThreadService.getByUser] Replaced "Untitled Thread" with:', {
              threadId: converted.id,
              originalName,
              finalName,
              usedDescription,
              description: thread.description
            });
          } else if (!originalName || originalName === 'Untitled Thread') {
            // Only log this warning in development and limit frequency to avoid performance issues
            if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
              console.warn('⚠️ [ThreadService.getByUser] Thread missing name or has "Untitled Thread" in backend response, using fallback (sample):', {
                threadId: converted.id,
                originalName,
                fallbackName: finalName
              });
            }
          }
        }
        
        return converted;
      });
      
      if (process.env.NODE_ENV === 'development') {
        console.log('📦 [ThreadService.getByUser] Converted threads:', {
          count: convertedThreads.length,
          threadIds: convertedThreads.map(t => ({ id: t.id, name: t.name || 'MISSING NAME', hasName: !!t.name }))
        });
      }
      
      return convertedThreads;
    } catch (error: any) {
      // Better error handling for 400 Bad Request
      if (error.response?.status === 400) {
        console.error('[ThreadService.getByUser] Invalid request format (400):', {
          userId,
          error: error.response?.data,
          message: 'Check API documentation for correct user_id format'
        });
      } else if (error.response?.status >= 500) {
        console.error('[ThreadService.getByUser] Server error:', error.response?.status);
      } else {
        console.error('[ThreadService.getByUser] Error fetching user threads:', error);
      }
      return [];
    }
  }

  /**
   * Get thread by ID
   */
  static async getById(threadId: string): Promise<ChatThread> {
    const backendThread = await this.get<any>(API_ENDPOINTS.THREADS.BY_ID(threadId));
    return chatThreadFromBackend(backendThread) as ChatThread;
  }

  /**
   * Create a new thread
   */
  static async create(threadData: Partial<ChatThread>): Promise<ChatThread> {
    const backendData = chatThreadToBackend(threadData);
    const raw = await this.post<any>(API_ENDPOINTS.THREADS.BASE, backendData);
    const payload = raw?.data ?? raw;
    return chatThreadFromBackend(payload) as ChatThread;
  }

  /**
   * Update thread (backend: PATCH /chat/threads/{id})
   */
  static async update(threadId: string, threadData: Partial<ChatThread>): Promise<ChatThread> {
    const backendData = chatThreadToBackend(threadData);
    const backendThread = await this.patch<any>(API_ENDPOINTS.THREADS.BY_ID(threadId), backendData);
    return chatThreadFromBackend(backendThread) as ChatThread;
  }

  /**
   * Delete thread
   */
  static async delete(threadId: string): Promise<void> {
    return this.delete(API_ENDPOINTS.THREADS.BY_ID(threadId));
  }

  /**
   * Get messages for a thread
   * GET /chat/threads/{thread_id}/messages
   * Handles response aliases: text/content, who (derived from message_type), timestamp/created_at
   */
  static async getMessages(
    threadId: string,
    options?: { limit?: number; offset?: number }
  ): Promise<ChatMessage[]> {
    try {
      const params = new URLSearchParams();
      if (options?.limit) {
        params.append('limit', options.limit.toString());
      }
      if (options?.offset) {
        params.append('offset', options.offset.toString());
      }
      
      const endpoint = `${API_ENDPOINTS.THREADS.MESSAGES(threadId)}${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await this.get<any>(endpoint);
      
      // Handle different response formats (similar to getByUser)
      let messagesArray: any[] = [];
      
      if (Array.isArray(response)) {
        messagesArray = response;
      } else if (response && typeof response === 'object') {
        if (Array.isArray(response.messages)) {
          messagesArray = response.messages;
        } else if (Array.isArray(response.data)) {
          messagesArray = response.data;
        } else if (response.data && typeof response.data === 'object') {
          if (Array.isArray(response.data.messages)) {
            messagesArray = response.data.messages;
          } else if (Array.isArray(response.data.data)) {
            messagesArray = response.data.data;
          }
        }
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log('📥 [ThreadService.getMessages] Raw response:', {
          threadId,
          responseType: typeof response,
          isArray: Array.isArray(response),
          messagesCount: messagesArray.length,
          responseSample: Array.isArray(response) ? response[0] : response
        });
      }
      
      // Transform backend messages to frontend format
      // Backend may use: content, message_type, created_at
      // Frontend expects: text, who, timestamp
      return messagesArray.map((msg: any) => {
        // Determine 'who' from message_type or user_id
        let who: 'user' | 'assistant' | 'system' = 'user';
        if (msg.message_type === 'assistant' || msg.message_type === 'system') {
          who = msg.message_type;
        } else if (msg.user_id === null || msg.user_id === undefined) {
          who = 'assistant';
        } else {
          who = 'user';
        }
        
        return {
          id: msg.id,
          threadId: msg.thread_id || threadId,
          who,
          text: msg.text || msg.content || '',
          timestamp: msg.timestamp || msg.created_at || new Date().toISOString(),
          type: (msg.type || msg.message_type || 'text') as ChatMessage['type'],
          metadata: msg.metadata || msg.message_metadata || {},
        } as ChatMessage;
      });
    } catch (error: any) {
      // Handle 404 as empty messages (thread might not have messages yet)
      if (error.response?.status === 404) {
        if (process.env.NODE_ENV === 'development') {
          console.log('📭 [ThreadService.getMessages] No messages found for thread:', threadId);
        }
        return [];
      }
      
      // Suppress CORS/network errors
      const isCorsError = error.code === 'ERR_NETWORK' || 
                         error.message?.includes('CORS') ||
                         error.message?.includes('Access-Control-Allow-Origin');
      if (!isCorsError) {
        console.error('❌ [ThreadService.getMessages] Error fetching thread messages:', {
          threadId,
          status: error.response?.status,
          error: error.message
        });
      }
      return [];
    }
  }

  /**
   * Send a message to a thread
   * POST /chat/threads/{thread_id}/messages
   * Supports request aliases: content/text/message, user_id/who/userId, type/message_type
   */
  static async sendMessage(
    threadId: string,
    message: Partial<ChatMessage> & { user_id?: string; content?: string; text?: string }
  ): Promise<ChatMessage> {
    // Build request body supporting all documented aliases
    const requestBody: Record<string, unknown> = {};
    
    // Content field (supports content, text, message aliases)
    if (message.content) {
      requestBody.content = message.content;
    } else if (message.text) {
      requestBody.content = message.text;
    } else if ((message as any).message) {
      requestBody.content = (message as any).message;
    } else {
      // Content is required
      throw new Error('Message content is required');
    }
    
    // User ID field (supports user_id, who, userId aliases)
    // CRITICAL: user_id is required by the backend
    if (message.user_id) {
      requestBody.user_id = message.user_id;
    } else if (message.who === 'user' && (message as any).userId) {
      requestBody.user_id = (message as any).userId;
    } else {
      // Log error if user_id is missing
      console.error('❌ [ThreadService.sendMessage] user_id is missing from message:', {
        threadId,
        messageKeys: Object.keys(message),
        hasUserId: !!message.user_id,
        hasWho: !!message.who,
        hasUserIdAlias: !!(message as any).userId
      });
      throw new Error('user_id is required to send a message');
    }
    
    // Validate user_id is a valid UUID format
    if (typeof requestBody.user_id !== 'string' || requestBody.user_id.trim() === '') {
      console.error('❌ [ThreadService.sendMessage] Invalid user_id format:', {
        threadId,
        user_id: requestBody.user_id,
        user_idType: typeof requestBody.user_id
      });
      throw new Error('user_id must be a non-empty string (UUID)');
    }
    
    // Type field (supports type, message_type aliases)
    if (message.type) {
      requestBody.type = message.type;
      requestBody.message_type = message.type;
    } else if ((message as any).message_type) {
      requestBody.type = (message as any).message_type;
      requestBody.message_type = (message as any).message_type;
    } else {
      requestBody.type = 'user';
      requestBody.message_type = 'user';
    }

    if (message.metadata && Object.keys(message.metadata).length > 0) {
      requestBody.metadata = message.metadata;
      requestBody.message_metadata = message.metadata;
    }
    
    try {
      const response = await this.post<any>(API_ENDPOINTS.THREADS.MESSAGES(threadId), requestBody);
      
      // Handle different response formats
      let messageData: any;
      if (response.data) {
        messageData = response.data;
      } else if (response.message_id) {
        // Response might have message_id and data separately
        messageData = response;
      } else {
        messageData = response;
      }
      
      // Transform to frontend format
      const who: 'user' | 'assistant' | 'system' = 
        messageData.who || 
        (messageData.message_type === 'assistant' || messageData.message_type === 'system' 
          ? messageData.message_type 
          : 'user');
      
      return {
        id: messageData.id || messageData.message_id || '',
        threadId: messageData.thread_id || threadId,
        who,
        text: messageData.text || messageData.content || '',
        timestamp: messageData.timestamp || messageData.created_at || new Date().toISOString(),
        type: (messageData.type || messageData.message_type || 'text') as ChatMessage['type'],
        metadata: messageData.metadata || messageData.message_metadata || {},
      } as ChatMessage;
    } catch (error: any) {
      // Handle 400 Bad Request (invalid format)
      if (error.response?.status === 400) {
        console.error('❌ [ThreadService.sendMessage] Invalid request format (400):', {
          threadId,
          error: error.response?.data,
          requestBody
        });
      }
      throw error;
    }
  }
}
